import {
  AfterViewInit, ApplicationRef, Component, CUSTOM_ELEMENTS_SCHEMA, ElementRef, EmbeddedViewRef,
  EventEmitter, Input, OnDestroy, OnInit, Output, TemplateRef, ViewChild, inject
} from '@angular/core';
import { PaymentIntent, StripeElementLocale, StripeElementsOptions } from '@stripe/stripe-js';
import { TranslateService } from '@ngx-translate/core';
import { StripeElementsDirective, StripeFactoryService, StripePaymentElementComponent, StripeServiceInterface } from 'ngx-stripe';
import { Subject, takeUntil } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { WIDGET_CONSTANTS } from '../../../constants/widget-constants';
import { AppointmentServiceDao } from '../../../db-models/appointment-service-dao';
import { CouponDbModel } from '../../../db-models/coupon-db.model';
import { CartItem, EventCartItem } from '../../../models/cart.model';
import { Partner } from '../../../models/global';
import { UserState } from '../../../models/state.model';
import { StripSubscriptionDetails } from '../../../models/stripe-token.model';
import { AppointmentService } from '../../../services/appointment.service';
import { LoggerService } from '../../../services/logger.service';
import { PaymentService } from '../../../services/payment.service';
import { WidgetUtilService } from '../../../services/widget-util.service';

const STRIPE_TEXT_LIMIT = 500;

@Component({
  selector: 'app-stripe',
  templateUrl: './stripe.component.html',
  styleUrls: ['./stripe.component.scss'],
  standalone: true,
  imports: [StripePaymentElementComponent, StripeElementsDirective],
  // `<slot>` is a native element, not an Angular one.
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class StripeComponent implements OnInit, AfterViewInit, OnDestroy {

  private static slotSequence = 0;
  private static readonly SLOT_CLASS = 'calenso-payment-slot';
  private static readonly HARDENING_STYLE_ID = 'calenso-payment-slot-hardening';

  @ViewChild(StripePaymentElementComponent) paymentElement!: StripePaymentElementComponent;
  @ViewChild('stripeView') private stripeViewRef: ElementRef<HTMLDivElement>;
  @ViewChild('stripeElements', { static: true }) private stripeElementsTpl!: TemplateRef<unknown>;

  /** Unique per instance so two payment surfaces can never project into the same slot. */
  protected readonly slotName = `calenso-payment-${StripeComponent.slotSequence++}`;

  @Input() partner: Partner;
  @Input() price: number;
  @Input() coupon: CouponDbModel;
  @Input() lang: string;
  @Input() protected userState: UserState;
  @Input() priceAfterCouponReduction: number;
  @Input() connectedAccountId: string;
  @Input() finalCouponDiscount: number;
  @Input() originalAmount: number;
  @Input() addonsPrice = 0;
  @Input() widgetType: string;
  @Input() cart: CartItem[];
  @Input() eventCart: EventCartItem[];
  @Input() appointmentServicesAvailable: AppointmentServiceDao[];
  @Input() set storageComplete(value: boolean) {
    if (value) {
      if (this.paymentIntent) {
        this.handlePayment();
      } else {
        this.handleError(this.translate.instant('payment_components.stripe_not_ready'));
      }
    }
  }

  @Output() stripeSuccessfulEvent = new EventEmitter<string>();
  @Output() resetStripeTokenEvent = new EventEmitter<void>();
  @Output() stripElementLoadEvent = new EventEmitter<boolean>();
  @Output() storeBookingItemsEvent = new EventEmitter<void>();

  private appointmentService = inject(AppointmentService);
  private paymentService = inject(PaymentService);
  private destroy$ = new Subject<void>();
  private stripeFactoryService = inject(StripeFactoryService);
  private translate = inject(TranslateService);
  private widgetUtilService = inject(WidgetUtilService);
  private elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private appRef = inject(ApplicationRef);
  private projectedView?: EmbeddedViewRef<unknown>;
  private slotHost?: HTMLElement;

  protected stripe!: StripeServiceInterface;

  protected elementsOptions: StripeElementsOptions = {
    locale: 'de',
  };
  protected stripeLoading = true;
  protected errorMessage: string;
  private _stripSubscriptionDetails = new StripSubscriptionDetails();
  private paymentIntent: PaymentIntent;
  private elementLoadTimeoutId: ReturnType<typeof setTimeout>;

  /**
   * The slotted wrapper itself is hardened from the shadow root via `::slotted()`, but that
   * selector cannot reach its descendants - so a host-page rule like `.x * { opacity: 0 }` could
   * still blank out Stripe's iframe. Stripe nests four wrappers between the slot host and the
   * iframe (`ngx-stripe-elements > ngx-stripe-payment > .field > .__PrivateStripeElement`), so the
   * guard has to cover every descendant, not just a direct child. It pins only the properties that
   * could hide, shrink or disable the form - never layout ones Stripe sizes itself.
   *
   * The class is doubled to raise specificity to (0,2,0), so an ordinary single-class host rule
   * cannot beat it by accident.
   *
   * Be clear about what this does NOT achieve - the comment used to claim more than it delivers:
   * it is best-effort, not a boundary. `display` is deliberately NOT pinned (forcing it on every
   * descendant would fight Stripe's own internal layout), so `display: none` still gets through.
   * And partner CSS is appended to `document.body` by `applyCustomCss()`, which is later in
   * document order than this `<head>` style, so it wins every specificity TIE. A partner rule with
   * two classes or an id beats this outright.
   *
   * The real boundary is elsewhere and does hold: the slot host itself is reset from inside the
   * shadow root by `::slotted()`, which beats any host rule regardless of specificity, and the card
   * fields live inside Stripe's cross-origin iframe where no page CSS can reach them at all.
   */
  private static hardenSlotAgainstHostPageCss(): void {
    if (document.getElementById(StripeComponent.HARDENING_STYLE_ID)) {
      return;
    }
    const style = document.createElement('style');
    style.id = StripeComponent.HARDENING_STYLE_ID;
    style.textContent = `
      .${StripeComponent.SLOT_CLASS}.${StripeComponent.SLOT_CLASS},
      .${StripeComponent.SLOT_CLASS}.${StripeComponent.SLOT_CLASS} * {
        opacity: 1 !important;
        visibility: inherit !important;
        filter: none !important;
        transform: none !important;
        pointer-events: auto !important;
        max-height: none !important;
        clip-path: none !important;
      }`;
    document.head.appendChild(style);
  }

  ngOnInit(): void {
    this.stripe = this.stripeFactoryService.create(environment.stripe.publishableKey, {
      stripeAccount: this.connectedAccountId
    });

    this.setLocale();
    this.paymentService.initiateStripeSubject
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.updatePaymentMethod());
    this.paymentService.recreatePaymentIntentSubject
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.recreatePaymentIntent());
    this.createPaymentIntent();
    // stripe.js can hang without firing ready OR loaderror (e.g. network stall);
    // without a deadline the parents' loading gate would never release.
    this.elementLoadTimeoutId = setTimeout(() => {
      if (this.stripeLoading) {
        this.handleError(this.translate.instant('payment_components.stripe_not_ready'));
      }
    }, 30000);
  }

  ngAfterViewInit(): void {
    this.projectStripeIntoLightDom();
  }

  ngOnDestroy(): void {
    clearTimeout(this.elementLoadTimeoutId);
    this.destroy$.next();
    this.destroy$.complete();
    if (this.projectedView) {
      this.appRef.detachView(this.projectedView);
      this.projectedView.destroy();
      this.projectedView = undefined;
    }
    this.slotHost?.remove();
    this.slotHost = undefined;
  }

  /**
   * Stripe Elements cannot mount inside a shadow root: Stripe.js resolves element nodes through
   * `document`, which cannot see into a shadow tree, so the Payment Element emits `loaderstart`
   * and then never `ready` - no error, no warning, the form simply never appears.
   *
   * A SLOTTED node is the way out. It is a direct child of the custom element, so it physically
   * lives in the LIGHT DOM and Stripe can find it, while rendering at the `<slot>` position inside
   * the widget's shadow layout. Everything else - the skeleton, the error alert - stays inside the
   * shadow root, so the light-DOM surface exposed to the host page is one wrapper whose only child
   * is Stripe's own cross-origin iframe.
   */
  private projectStripeIntoLightDom(): void {
    const view = this.stripeElementsTpl.createEmbeddedView({});
    // attachView() is load-bearing for PAYMENTS, not just change detection: it registers the view in
    // the declaration container's MOVED_VIEWS, which is what keeps `@ViewChild(StripePaymentElement)`
    // resolving. Without it `confirmStripePayment()` dereferences an undefined `elements`.
    this.appRef.attachView(view);
    this.projectedView = view;

    const root = this.elementRef.nativeElement.getRootNode();
    const target = this.resolveProjectionTarget(root);
    view.rootNodes.forEach((node: Node) => target.appendChild(node));
  }

  /**
   * The slotted host inside the custom element when the widget runs in a shadow root, otherwise
   * (unit tests, non-custom-element usage) the in-place container - there is no boundary to cross.
   */
  private resolveProjectionTarget(root: Node | ShadowRoot): HTMLElement {
    if (typeof ShadowRoot === 'undefined' || !(root instanceof ShadowRoot)) {
      return this.stripeViewRef?.nativeElement ?? this.elementRef.nativeElement;
    }
    StripeComponent.hardenSlotAgainstHostPageCss();
    const slotHost = document.createElement('div');
    slotHost.setAttribute('slot', this.slotName);
    slotHost.className = StripeComponent.SLOT_CLASS;
    (root.host as HTMLElement).appendChild(slotHost);
    this.slotHost = slotHost;
    return slotHost;
  }


  protected onReady(): void {
    clearTimeout(this.elementLoadTimeoutId);
    this.stripeLoading = false;
    // A remount after a failed load (recreatePaymentIntent) must not leave the previous
    // load error banner sitting above a form that now works.
    this.errorMessage = undefined;
    this.stripElementLoadEvent.emit(true);
  }

  protected onLoadError(event: any): void {
    clearTimeout(this.elementLoadTimeoutId);
    LoggerService.warn('[stripe error]', event);
    // Clear the skeleton and surface the failure. Without this, any Stripe load error left the
    // ghost bars up forever with no message — the customer just saw an empty payment box.
    this.stripeLoading = false;
    this.errorMessage = event?.error?.message || 'Failed to load the payment form. Please try again.';
    this.stripElementLoadEvent.emit(false);
    this.handleError(this.translate.instant('payment_components.stripe_not_ready'), event);
  }

  private setLocale(): void {
    if (this.lang) {
      this.elementsOptions.locale = this.lang.split('_')?.[0] as StripeElementLocale;
    }
  }

  private createPaymentIntent(): void {
    this.updateSubscriptionDetails();
    this.paymentService.createPaymentIntent(this._stripSubscriptionDetails)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (paymentIntent) => {
          LoggerService.log('PaymentIntent created:', paymentIntent);
          this.paymentIntent = paymentIntent;
          this._stripSubscriptionDetails.payment_intent_id = paymentIntent.id;
          this.elementsOptions.clientSecret = this.paymentIntent.client_secret;
        },
        error: error => this.handleError('Failed to initialize the payment form. Please try again.', error)
      });
  }

  protected updatePaymentMethod(): void {
    if (!this.paymentElement?.elements) {
      this.handleError(this.translate.instant('payment_components.stripe_not_ready'));
      return;
    }
    this.errorMessage = undefined;
    this.updateSubscriptionDetails();
    this.paymentService.updatePaymentIntent(this._stripSubscriptionDetails)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.triggerStorageEvent();
        },
        error: error => this.handleError('Failed to update your payment details. Please try again.', error)
      });
  }

  private triggerStorageEvent(): void {
    if (this.paymentIntent) {
      // Emit event to trigger bookingItems storage in parent component
      // Parent will set storageComplete=true when done, which triggers handlePayment()
      this.storeBookingItemsEvent.emit();
    } else {
      this.handleError(this.translate.instant('payment_components.stripe_not_ready'));
    }
  }

  private handlePayment(): void {
    if (!this.paymentElement?.elements) {
      this.handleError(this.translate.instant('payment_components.stripe_not_ready'));
      return;
    }
    this.confirmStripePayment();
  }

  private confirmStripePayment(): void {
    try {
      this.stripe.confirmPayment({
        elements: this.paymentElement.elements,
        confirmParams: {
          return_url: this.widgetUtilService.getStripeReturnUrl(),
        },
        redirect: 'if_required'
      }).subscribe({
        next: result => {
          if (result.error) {
            this.errorMessage = result.error.message;
            this.resetStripeTokenEvent.emit();
            LoggerService.error('Payment confirmation failed:', result.error.message);
            this.scrollToStripeView();
            // Don't recreate the PaymentIntent on confirm errors. Stripe leaves
            // the PI in requires_payment_method state for client-side errors
            // (incomplete card, declined card, 3DS failure) so the customer can
            // fix the form and retry on the same PI. Recreating destroys the
            // customer's input and fires a wasteful create call.
          } else {
            this.stripeSuccessfulEvent.emit(result.paymentIntent.id);
          }
        },
        error: err => this.handleError(this.translate.instant('payment_components.payment_processing_error'), err)
      });
    } catch (err) {
      this.handleError(this.translate.instant('payment_components.payment_processing_error'), err);
    }
  }

  private scrollToStripeView(): void {
    this.stripeViewRef?.nativeElement?.scrollIntoView({
      behavior: 'smooth',
      block: 'center'
    });
  }

  private recreatePaymentIntent(): void {
    this.paymentIntent = null;
    this._stripSubscriptionDetails.payment_intent_id = null;
    // Force Stripe Elements to unmount before remount. The template's @if
    // guards on elementsOptions.clientSecret — nulling it destroys the iframe
    // that still holds the stale card data + old client_secret. Without this
    // step, confirmPayment() on the next click hangs because the cached
    // elements instance is tied to a PI Stripe no longer recognises.
    this.elementsOptions.clientSecret = undefined;
    this.stripeLoading = true;
    this.errorMessage = undefined;
    this.createPaymentIntent();
  }

  private handleError(message: string, error?: any): void {
    LoggerService.error(message, error);
    this.resetStripeTokenEvent.emit();
    this.errorMessage = error?.error?.errors?.[0]?.message || message;
    this.stripeLoading = false;
  }

  private updateSubscriptionDetails(): void {
    const bookedItems = this.getBookedItemsForStripe();
    this._stripSubscriptionDetails.currency = this.partner.currency;
    this._stripSubscriptionDetails.amount = Math.round(this.price * 100);
    this._stripSubscriptionDetails.connected_account_id = this.connectedAccountId;
    this._stripSubscriptionDetails.automatic_payment_methods = { allow_redirects: 'always', enabled: true };
    if (this.paymentIntent) {
      this._stripSubscriptionDetails.payment_intent_id = this.paymentIntent.id;
    }

    this._stripSubscriptionDetails.metadata = {
      email: this.userState?.eMail || '',
      prename: this.userState?.forename || '',
      lastname: this.userState?.lastName || '',
      phone: this.userState?.phone || '',
      mobile: this.userState?.mobile || '',
      partner_id: this.partner.id,
      partner_name: this.partner.bookingName,
      ...(this.addonsPrice > 0 ? { addons_price: this.addonsPrice } : {}),
      ...bookedItems.metadata,
    };
    this._stripSubscriptionDetails.description = bookedItems.description;

    if (this.coupon) {
      this._stripSubscriptionDetails.metadata = {
        ...this._stripSubscriptionDetails.metadata,
        coupon_code: this.coupon.code,
        original_amount: this.originalAmount,
        is_fixed_amount: this.coupon.is_fixed_amount.toString(),
        is_percentage: this.coupon.is_percentage.toString(),
        coupon_amount: this.coupon.fixed_amount,
        coupon_percentage: this.coupon.percentage,
        coupon_final_discount: this.finalCouponDiscount,
        coupon_discount_amount: this.priceAfterCouponReduction,
        coupon_id: this.coupon.id,
      };
    }
  }

  /**
   * What the customer is paying for, so the partner can identify the booking from Stripe
   * alone: the booked service names (appointments) or event titles (events) become the
   * PaymentIntent description and a metadata pair. Same source as the Saferpay description.
   */
  private getBookedItemsForStripe(): { description?: string; metadata: Record<string, string> } {
    try {
      return this.resolveBookedItems();
    } catch (error) {
      // A dashboard label must never stand between the customer and the payment form: an
      // unexpected cart shape degrades to a PaymentIntent without a description, not to no PaymentIntent.
      LoggerService.warn('[stripe] could not resolve the booked items for the PaymentIntent description', error);
      return { metadata: {} };
    }
  }

  private resolveBookedItems(): { description?: string; metadata: Record<string, string> } {
    const isEvent = this.widgetType === WIDGET_CONSTANTS.EVENT || this.widgetType === WIDGET_CONSTANTS.EVENTS;
    const names: string[] = [];
    const ids: string[] = [];

    if (isEvent) {
      this.eventCart?.forEach(cartItem => {
        if (cartItem.currentEvent?.title) {
          names.push(cartItem.currentEvent.title);
          ids.push(String(cartItem.eventId));
        }
      });
    } else {
      this.cart?.forEach(cartItem => {
        const services: AppointmentServiceDao[] = this.appointmentService.getServicesByIds(
          { serviceIds: cartItem.servicesIds ?? [], returnKey: 'services' },
          this.appointmentServicesAvailable
        ) ?? [];
        services.forEach(service => {
          if (service?.name) {
            names.push(service.name);
            ids.push(String(service.id));
          }
        });
      });
    }

    const metadata: Record<string, string> = {};
    if (this.widgetType) {
      metadata['widget_type'] = this.widgetType;
    }
    if (names.length) {
      metadata[isEvent ? 'event_titles' : 'service_names'] = this.truncateForStripe(names.join(', '));
      metadata[isEvent ? 'event_ids' : 'service_ids'] = this.truncateForStripe(ids.join(','));
    }

    return {
      description: names.length ? this.truncateForStripe(names.join(', ')) : undefined,
      metadata,
    };
  }

  /**
   * Stripe caps every metadata value at 500 characters; the description gets the same cap so a
   * booking with many services can never fail the PaymentIntent call on length. Cut on a code
   * point, not a UTF-16 unit: a slice through an emoji leaves a lone surrogate that the backend's
   * JSON parser rejects, which would fail the whole PaymentIntent request.
   */
  private truncateForStripe(value: string): string {
    return Array.from(value).slice(0, STRIPE_TEXT_LIMIT).join('');
  }
}
